export type Scale = ((value: number) => number) & { invert: (position: number) => number };

/** Linear map from domain [d0, d1] to range [r0, r1] with an inverse. */
export function linearScale(d0: number, d1: number, r0: number, r1: number): Scale {
  const span = d1 - d0;
  const ratio = span === 0 ? 0 : (r1 - r0) / span;
  const scale = ((value: number) => r0 + (value - d0) * ratio) as Scale;
  scale.invert = (position: number) => (ratio === 0 ? d0 : d0 + (position - r0) / ratio);
  return scale;
}

const STEP_BASES = [1, 2, 2.5, 5, 10];

function decimalsFor(step: number): number {
  const exponent = Math.floor(Math.log10(step));
  return Math.max(0, -exponent + 1);
}

/**
 * "Nice" axis ticks with a 1/2/2.5/5 step. Always starts at 0 when `min >= 0`.
 * Returns ascending tick values covering [min, max].
 */
export function niceTicks(min: number, max: number, count: number): number[] {
  const safeCount = Math.max(1, Math.floor(count));
  let lo = Math.min(min, max);
  let hi = Math.max(min, max);
  if (lo >= 0) lo = 0;
  if (hi <= lo) hi = lo + 1;

  const rawStep = (hi - lo) / safeCount;
  const exponent = Math.floor(Math.log10(rawStep));
  const magnitude = 10 ** exponent;
  const base = rawStep / magnitude;
  const stepBase = STEP_BASES.find(candidate => candidate >= base - 1e-9) ?? 10;
  const step = stepBase * magnitude;
  const decimals = decimalsFor(step);

  const start = Math.floor(lo / step + 1e-9) * step;
  const end = Math.ceil(hi / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let value = start; value <= end + step / 2; value += step) {
    ticks.push(Number(value.toFixed(decimals)));
  }
  return ticks;
}

function thin(indices: number[], maxLabels: number): number[] {
  if (maxLabels <= 0) return [];
  if (indices.length <= maxLabels) return indices;
  const every = Math.ceil(indices.length / maxLabels);
  return indices.filter((_, position) => position % every === 0);
}

/**
 * Indices of January months (`dateLabel` ending with `-01`), thinned evenly to at most `maxLabels`.
 * Falls back to evenly spaced indices when the schedule has no January.
 */
export function yearTicks(months: ReadonlyArray<{ dateLabel: string }>, maxLabels: number): number[] {
  if (!months.length) return [];
  const januaries: number[] = [];
  months.forEach((month, index) => {
    if (month.dateLabel.endsWith('-01')) januaries.push(index);
  });
  if (januaries.length) return thin(januaries, maxLabels);

  const count = Math.min(maxLabels, months.length);
  if (count <= 1) return [0];
  const step = (months.length - 1) / (count - 1);
  const evenly: number[] = [];
  for (let i = 0; i < count; i += 1) {
    const index = Math.round(i * step);
    if (evenly[evenly.length - 1] !== index) evenly.push(index);
  }
  return evenly;
}
